import { makeStyles } from "@material-ui/core/styles";
import { RiskQuestionnaire } from "@/types/kycReview";
import {
  computeRiskScore,
  RiskQuestion,
  riskProfileDetails,
  riskQuestions,
  selectedRiskOption,
} from "../logic/riskProfiler";
import { rv } from "../tokens";
import { AlertCircleIcon } from "./icons";

const useStyles = makeStyles((theme) => ({
  intro: { color: rv.muted, fontSize: 14, marginBottom: theme.spacing(3) },
  question: { marginBottom: theme.spacing(3) },
  qLabel: {
    fontWeight: 600,
    fontSize: 14.5,
    color: rv.heading,
    marginBottom: theme.spacing(1.25),
  },
  cards: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: theme.spacing(1.5),
    [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
  },
  card: {
    textAlign: "left",
    cursor: "pointer",
    border: `1.5px solid ${rv.hairline}`,
    borderRadius: 12,
    background: "#fff",
    padding: theme.spacing(1.5, 1.75),
    fontSize: 13,
    lineHeight: 1.45,
    color: rv.body,
    font: "inherit",
    transition: "all .15s ease",
    minHeight: 64,
    "&:hover": { borderColor: rv.green500 },
  },
  cardSelected: {
    background: rv.gold,
    color: rv.onDark,
    borderColor: rv.gold,
  },
  error: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    color: rv.errorFg,
    fontSize: 12.5,
    fontWeight: 500,
    marginTop: theme.spacing(1),
  },
  result: {
    marginTop: theme.spacing(3),
    borderLeft: `4px solid ${rv.green}`,
    background: rv.mint,
    borderRadius: 12,
    padding: theme.spacing(2.5),
  },
  resultEyebrow: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: ".06em",
    color: rv.green,
  },
  resultLabel: {
    fontWeight: 700,
    fontSize: 20,
    color: rv.green,
    margin: theme.spacing(0.5, 0, 1),
  },
  resultDesc: { color: rv.body, fontSize: 13.5, lineHeight: 1.55 },
  resultFundsTitle: {
    fontWeight: 700,
    fontSize: 12.5,
    color: rv.heading,
    margin: theme.spacing(1.5, 0, 0.5),
  },
  resultFund: { color: rv.muted, fontSize: 13 },
}));

interface Props {
  questionnaire: RiskQuestionnaire;
  result: string;
  error?: string;
  onAnswer: (question: RiskQuestion, selected: number) => void;
}

export default function RiskProfiler({
  questionnaire,
  result,
  error,
  onAnswer,
}: Props) {
  const classes = useStyles();
  const detail = result ? riskProfileDetails[result] : undefined;

  return (
    <div>
      <p className={classes.intro}>
        If your investment preferences have changed, update your answers. Your
        result appears automatically once all questions are answered.
      </p>

      {riskQuestions.map((question, index) => {
        const selected = selectedRiskOption(questionnaire, question);
        return (
          <div className={classes.question} key={question.category}>
            <div className={classes.qLabel}>
              {index + 1}. {question.label}
            </div>
            <div className={classes.cards}>
              {question.options.map((option, optionIndex) => {
                const optionValue = optionIndex + 1;
                const isSelected = selected === optionValue;
                return (
                  <button
                    type="button"
                    key={option}
                    className={`${classes.card} ${
                      isSelected ? classes.cardSelected : ""
                    }`}
                    onClick={() => onAnswer(question, optionValue)}
                    aria-pressed={isSelected}
                  >
                    {option}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}

      {error && (
        <div className={classes.error} data-rv-error="true">
          <AlertCircleIcon size={14} />
          {error}
        </div>
      )}

      {detail && (
        <div className={classes.result}>
          <div className={classes.resultEyebrow}>
            Your risk profile · {computeRiskScore(questionnaire)} of 27
          </div>
          <div className={classes.resultLabel}>{detail.label}</div>
          <div className={classes.resultDesc}>{detail.description}</div>
          <div className={classes.resultFundsTitle}>Recommended fund types</div>
          {detail.recommendedFunds.map((fund) => (
            <div className={classes.resultFund} key={fund}>
              {fund}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
